import _ from 'lodash';

const CommonHelper = {
    saveBlobAsFile(blob: Blob, fileName: string) {
        /* 
            This setTimout is a workaround for a bug in Safari
            https://developer.apple.com/forums/thread/115102
            500ms is not working, so I used 1000ms.
        */
        setTimeout(() => {
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            link.click();
            URL.revokeObjectURL(blobUrl);
        }, 1000);
    },

    getFileName: (headers: any, extension?: string): string => {
        let fileName = headers?.[`content-disposition`]?.split?.(`filename=`)?.[1] ?? `document.${extension ?? 'pdf'}`;
        if (fileName.startsWith('"') && fileName.endsWith('"')) {
            fileName = fileName.substring(1, fileName.length - 1);
        }
        return fileName;
    },

    async copyToClipboard(text: any) {
        if ((window as any)?.clipboardData?.setData) {
            return (window as any)?.clipboardData?.setData?.('Text', text);
        }
        if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
            const textarea = document.createElement('textarea');
            textarea.textContent = text;
            textarea.style.position = 'fixed';
            document.body.appendChild(textarea);
            textarea.select();
            try {
                return document.execCommand('copy');
            } catch (ex) {
                console.warn('Copy to clipboard failed.', ex);
                return false;
            } finally {
                document.body.removeChild(textarea);
            }
        }
    },

    objectStringToPath: (path: string) => {
        return (
            path
                ?.replaceAll?.(`[`, `.`)
                ?.replaceAll?.(/[?\]"'`]/g, ``)
                ?.replaceAll?.(`..`, `.`)
                ?.split?.(`.`)
                ?.filter?.((i: any) => i?.length) ?? []
        );
    },

    objectFlatten: (obj: any): any => {
        Object.keys(obj)
            ?.filter?.((key) => typeof obj[key] === `object`)
            .forEach?.((key) => {
                Object.keys(obj?.[key] ?? {})?.forEach?.((innerKey) => {
                    obj[`${key}.${innerKey}`] = obj[key][innerKey];
                });
                obj = _.omit(obj, key);
            });
        return Object.keys(obj)?.filter?.((key) => typeof obj[key] === `object`).length > 0
            ? CommonHelper.objectFlatten(obj)
            : obj;
    },

    objectNested: (obj: any): any => {
        const newObj = {};
        Object.keys(obj).forEach((key) => {
            _.set(newObj, key, obj[key]);
        });
        return newObj;
    },

    objectDiff: (o1: any = {}, o2: any = {}) => {
        const o1flatten = CommonHelper.objectFlatten(o1);
        const o2flatten = CommonHelper.objectFlatten(o2);
        return {
            incoming: CommonHelper.objectNested(
                Object.fromEntries(
                    Object.keys(o2flatten)
                        .filter(
                            (key) =>
                                !Object.prototype.hasOwnProperty.call(o1flatten, key) ||
                                o1flatten[key] !== o2flatten[key],
                        )
                        .map((key) => [key, o2flatten[key]]),
                ),
            ),
            outcoming: CommonHelper.objectNested(
                Object.fromEntries(
                    Object.keys(o1flatten)
                        .filter(
                            (key) =>
                                !Object.prototype.hasOwnProperty.call(o2flatten, key) ||
                                o2flatten[key] !== o1flatten[key],
                        )
                        .map((key) => [key, o1flatten[key]]),
                ),
            ),
            changes: {
                prev: CommonHelper.objectNested(
                    Object.fromEntries(
                        Object.keys(o1flatten)
                            .filter(
                                (key) =>
                                    Object.prototype.hasOwnProperty.call(o2flatten, key) &&
                                    o1flatten?.[key] !== o2flatten?.[key],
                            )
                            .map((key) => [key, o1flatten[key]]),
                    ),
                ),
                next: CommonHelper.objectNested(
                    Object.fromEntries(
                        Object.keys(o1flatten)
                            .filter(
                                (key) =>
                                    Object.prototype.hasOwnProperty.call(o2flatten, key) &&
                                    o1flatten?.[key] !== o2flatten?.[key],
                            )
                            .map((key) => [key, o2flatten[key]]),
                    ),
                ),
                fresh: CommonHelper.objectNested(
                    Object.fromEntries(
                        Object.keys(o2flatten)
                            .filter((key) => !Object.prototype.hasOwnProperty.call(o1flatten, key))
                            .map((key) => [key, o2flatten[key]]),
                    ),
                ),
                revoked: CommonHelper.objectNested(
                    Object.fromEntries(
                        Object.keys(o1flatten)
                            .filter((key) => !Object.prototype.hasOwnProperty.call(o2flatten, key))
                            .map((key) => [key, o1flatten[key]]),
                    ),
                ),
                flatten: Object.fromEntries(
                    Object.keys(o1flatten)
                        .filter(
                            (key) =>
                                Object.prototype.hasOwnProperty.call(o2flatten, key) &&
                                o1flatten?.[key] !== o2flatten?.[key],
                        )
                        .map((key) => [key, { prev: o1flatten[key], next: o2flatten[key] }]),
                ),
            },
        };
    },

    filterTrueValues: (obj: any) =>
        objectNested(Object.fromEntries(Object.entries(objectFlatten(obj)).filter(([_key, value]) => !!value))),

    queryParser: (search: any) =>
        Object.fromEntries(
            search
                .replace(/^\?/, '')
                .split('&')
                .map((i: any) => i.split(`=`))
                .map((i: any) => [i[0], decodeURI(i[1])]),
        ),

    fileToBase64: (file: any): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });
    },

    getElementClassPath: (element: any) => {
        const path = [];
        path.push(element?.className);
        const goToParent = (element: any) => {
            path.push(element?.className);
            if (element?.parentNode) {
                goToParent(element?.parentNode);
            }
        };
        goToParent(element);
        return path;
    },

    getElementParentsPath: (element: any) => {
        const path = [];
        path.push(element);
        const goToParent = (element: any) => {
            path.push(element);
            if (element?.parentNode) {
                goToParent(element?.parentNode);
            }
        };
        goToParent(element);
        return path;
    },

    QSToObject: (search: string): Record<string, string> => {
        const str = (
            '{"' +
            decodeURI(search.replace(/^\?&/, '').replace(/^\?/, ''))
                .replace(/"/g, '\\"')
                .replace(/&/g, '","')
                .replace(/=/g, '":"') +
            '"}'
        )
            .replace(/""/g, '')
            .replaceAll(':,', ':"",');

        try {
            const result = JSON.parse(str);
            for (const key in result) {
                result[key] = decodeURIComponent(result[key]);
            }
            return result;
        } catch (e) {
            console.error('Cannot parse query string:', search);
            return {};
        }
    },

    objectToQS: (obj = {}) => `?` + new URLSearchParams(obj).toString(),

    stringImposition: (s1: string, s2: string): boolean =>
        s1
            .trim()
            .toLowerCase()
            .includes(s2.trim().toLowerCase() || '') || false,

    opposite: (value: boolean) => !value,
};

export default CommonHelper;

export const queryParser = CommonHelper.queryParser;

export const opposite = CommonHelper.opposite;

export const isna = (item: any) => [undefined, null, ''].includes(item);

export const createId = () =>
    Math.random()
        .toString(36)
        .substring(3)
        .split(``)
        .map((i) => i[Math.random() > 0.5 ? `toLowerCase` : `toUpperCase`]())
        .join(``);

export const {
    objectDiff,
    objectFlatten,
    objectNested,
    fileToBase64,
    getElementClassPath,
    getElementParentsPath,
    QSToObject,
    objectToQS,
    saveBlobAsFile,
    getFileName,
    stringImposition,
    copyToClipboard,
    filterTrueValues,
    objectStringToPath,
} = CommonHelper;

export const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const togglePush = <T>(array: T[], value: T): T[] => {
    return [...array, value].filter((i) => (array?.includes?.(value) ? i !== value : true));
};
